import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ChartBarIcon,
  EyeIcon,
  GlobeAltIcon,
  CalendarIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

interface URLStats {
  short_code: string;
  destination_url: string;
  short_url: string;
  total_clicks: number;
  unique_clicks: number;
  click_rate: number;
  created_at: string;
  last_clicked: string | null;
  geographic_data: {
    [country: string]: number;
  };
  referrer_data: {
    [referrer: string]: number;
  };
  daily_clicks: {
    [date: string]: number;
  };
}

interface OverallStats {
  total_urls: number;
  total_clicks: number;
  average_click_rate: number;
  top_performing_url: string | null;
  clicks_this_week: number;
  clicks_last_week: number;
}

const Analytics: React.FC = () => {
  const { getIdToken } = useAuth();
  const [selectedUrl, setSelectedUrl] = useState<string>('');
  const [urlStats, setUrlStats] = useState<URLStats | null>(null);
  const [overallStats, setOverallStats] = useState<OverallStats | null>(null);
  const [urls, setUrls] = useState<Array<{
    short_code: string;
    short_url: string;
    destination_url: string;
    access_count?: number;
    created_at?: string;
    is_active?: boolean;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUrls();
  }, []);

  useEffect(() => {
    if (selectedUrl) {
      fetchUrlStats(selectedUrl);
    }
  }, [selectedUrl, urls]);

  const fetchUrls = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/urls`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure data is an array
        const urlsArray = Array.isArray(data) ? data : [];
        setUrls(urlsArray);
        if (urlsArray.length > 0 && !selectedUrl) {
          setSelectedUrl(urlsArray[0].short_code);
        }
        // Calculate overall stats after URLs are loaded
        fetchOverallStatsFromUrls(urlsArray);
      } else {
        setError('Failed to fetch URLs');
      }
    } catch (error) {
      setError('Failed to fetch URLs');
    } finally {
      setLoading(false);
    }
  };

  const fetchUrlStats = async (shortCode: string) => {
    // Use real data from the URLs we already fetched
    const selectedUrlData = urls.find(url => url.short_code === shortCode);
    if (selectedUrlData) {
      const totalClicks = selectedUrlData.access_count || 0;
      const createdAt = selectedUrlData.created_at || new Date().toISOString();

      setUrlStats({
        short_code: shortCode,
        destination_url: selectedUrlData.destination_url,
        short_url: selectedUrlData.short_url,
        total_clicks: totalClicks,
        unique_clicks: totalClicks, // For now, assume all clicks are unique
        click_rate: totalClicks > 0 ? 100 : 0, // Simple click rate calculation
        created_at: createdAt,
        last_clicked: totalClicks > 0 ? createdAt : null, // Approximate last click
        geographic_data: {},
        referrer_data: {},
        daily_clicks: {}
      });
    }
  };

  const fetchOverallStatsFromUrls = (urlsArray: typeof urls) => {
    // Calculate real stats from URLs data
    const totalClicks = urlsArray.reduce((sum, url) => sum + (url.access_count || 0), 0);
    const totalUrls = urlsArray.length;

    // Find top performing URL
    const topUrl = urlsArray.reduce((top, url) => {
      return (url.access_count || 0) > (top.access_count || 0) ? url : top;
    }, urlsArray[0]);

    setOverallStats({
      total_urls: totalUrls,
      total_clicks: totalClicks,
      average_click_rate: totalUrls > 0 ? (totalClicks / totalUrls) : 0,
      top_performing_url: topUrl ? topUrl.short_url : null,
      clicks_this_week: totalClicks, // For now, assume all clicks are recent
      clicks_last_week: 0,
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTopCountries = (geoData: { [country: string]: number } | undefined) => {
    if (!geoData) return [];
    return Object.entries(geoData)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);
  };

  const getTopReferrers = (referrerData: { [referrer: string]: number } | undefined) => {
    if (!referrerData) return [];
    return Object.entries(referrerData)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);
  };

  const getWeeklyChange = () => {
    if (!overallStats || !overallStats.clicks_last_week || overallStats.clicks_last_week === 0) return 0;
    const thisWeek = overallStats.clicks_this_week || 0;
    const lastWeek = overallStats.clicks_last_week || 0;
    return ((thisWeek - lastWeek) / lastWeek) * 100;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Analytics</h1>
        <p className="mt-2 text-sm text-gray-700">
          Track the performance of your short URLs
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-macos text-sm mb-6">
          {error}
        </div>
      )}

      {urls.length === 0 ? (
        <div className="bg-white rounded-macos-lg shadow-macos border border-gray-100 p-6">
          <div className="text-center py-12">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No analytics data</h3>
            <p className="mt-1 text-sm text-gray-500">Create some URLs first to see analytics.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Overall Stats */}
          {overallStats && (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
              <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-macos bg-blue-50 text-blue-500">
                      <ChartBarIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total URLs</p>
                    <p className="text-2xl font-semibold text-gray-900">{overallStats.total_urls}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-macos bg-green-50 text-green-500">
                      <EyeIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Clicks</p>
                    <p className="text-2xl font-semibold text-gray-900">{overallStats.total_clicks}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-macos bg-purple-50 text-purple-500">
                      <ArrowTrendingUpIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Avg. Click Rate</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {(overallStats.average_click_rate || 0).toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-macos bg-orange-50 text-orange-500">
                      <CalendarIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Weekly Change</p>
                    <div className="flex items-baseline">
                      <p className="text-2xl font-semibold text-gray-900">
                        {getWeeklyChange() > 0 ? '+' : ''}{(getWeeklyChange() || 0).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* URL Selector */}
          <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100 mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Select URL to Analyze</h3>
            <select
              value={selectedUrl}
              onChange={(e) => setSelectedUrl(e.target.value)}
              className="block w-full px-4 py-3 border border-gray-300 rounded-macos bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              style={{ backgroundColor: 'white', color: '#111827' }}
            >
              {Array.isArray(urls) && urls.map((url) => (
                <option
                  key={url.short_code}
                  value={url.short_code}
                  className="bg-white text-gray-900 py-2"
                  style={{ backgroundColor: 'white', color: '#111827' }}
                >
                  {url.short_url} → {url.destination_url.length > 50 ? url.destination_url.substring(0, 50) + '...' : url.destination_url}
                </option>
              ))}
            </select>
          </div>

          {/* URL-Specific Analytics */}
          {urlStats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* URL Performance */}
              <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100">
                <h3 className="text-lg font-medium text-gray-900 mb-4">URL Performance</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Clicks</span>
                    <span className="text-lg font-semibold text-gray-900">{urlStats.total_clicks}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Unique Clicks</span>
                    <span className="text-lg font-semibold text-gray-900">{urlStats.unique_clicks}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Click Rate</span>
                    <span className="text-lg font-semibold text-gray-900">{(urlStats.click_rate || 0).toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Created</span>
                    <span className="text-sm text-gray-900">{formatDate(urlStats.created_at)}</span>
                  </div>
                  {urlStats.last_clicked && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Last Clicked</span>
                      <span className="text-sm text-gray-900">{formatDate(urlStats.last_clicked)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Geographic Data */}
              <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Top Countries</h3>
                {Object.keys(urlStats.geographic_data || {}).length === 0 ? (
                  <p className="text-sm text-gray-500">No geographic data available</p>
                ) : (
                  <div className="space-y-3">
                    {getTopCountries(urlStats.geographic_data).map(([country, clicks]) => (
                      <div key={country} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <GlobeAltIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">{country}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{clicks}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Referrer Data */}
          {urlStats && Object.keys(urlStats.referrer_data || {}).length > 0 && (
            <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100 mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Referrers</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getTopReferrers(urlStats.referrer_data).map(([referrer, clicks]) => (
                  <div key={referrer} className="flex items-center justify-between p-3 bg-gray-50 rounded-macos">
                    <span className="text-sm text-gray-900 truncate mr-2">
                      {referrer === 'direct' ? 'Direct Traffic' : referrer}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{clicks}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Daily Clicks Chart Placeholder */}
          {urlStats && Object.keys(urlStats.daily_clicks || {}).length > 0 && (
            <div className="bg-white rounded-macos-lg shadow-macos p-6 border border-gray-100">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Daily Clicks (Last 30 Days)</h3>
              <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-macos">
                <div className="text-center">
                  <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">Chart visualization would go here</p>
                  <p className="text-xs text-gray-400">
                    Total clicks in period: {Object.values(urlStats.daily_clicks || {}).reduce((a, b) => a + b, 0)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Analytics;